import { NamedContentEntryText } from '../../components/ContentEntryText/NamedContentEntryText';
import { RobotAvatar } from '../../components/Props/RobotAvatar/RobotAvatar';
import type { ReactNode } from 'react';
import type { ContentEntryRefsKey } from '../../contentEntryRefs/contentEntryRefs';
import { capitalize } from '../string/capitalize';

export enum UserLevels {
  vulnerable = 'Vulnerable',
  determined = 'Determined',
  solid = 'Solid',
  armored = 'Armored',
  unbreakable = 'Unbreakable',
}

export const NO_USER_LEVEL = 'None';
export type UserLevelId = UserLevels | typeof NO_USER_LEVEL;
export const NO_USER_LEVEL_NUMBER = 0;

export const NoUserLevelTag = () => (
  <>
    <NamedContentEntryText container="span" refKey="leaderboard" subKey="level" /> {NO_USER_LEVEL_NUMBER}
  </>
);

const UserLevelsColorMap = {
  [UserLevels.vulnerable]: '#FF6F5A',
  [UserLevels.determined]: '#F8AA30',
  [UserLevels.solid]: '#4C5AFF',
  [UserLevels.armored]: '#3ABF87',
  [UserLevels.unbreakable]: '#6600FF',
};

// Soft-tinted background/text pairs used for the level-name pill (per Figma CyberQ Portal design).
const UserLevelsNamePillColorMap: Record<UserLevels, { background: string; color: string }> = {
  [UserLevels.vulnerable]: { background: '#FFEFEF', color: '#933547' },
  [UserLevels.determined]: { background: '#FFF7E6', color: '#762A11' },
  [UserLevels.solid]: { background: '#ECF8F6', color: '#0F4E3C' },
  [UserLevels.armored]: { background: '#ECF2FF', color: '#4C5AFF' },
  [UserLevels.unbreakable]: { background: '#EAE4FF', color: '#6600FF' },
};

export type UserLevelDetailsType = Record<
  UserLevels,
  {
    number: number;
    level: ReactNode;
    name: ReactNode;
    introText: ReactNode;
    description: ReactNode;
    image: ReactNode;
    color: string;
    namePillBackground: string;
    namePillColor: string;
  }
>;

export const UserLevelDetails = Object.fromEntries(
  Object.values(UserLevels).map((level, index) => {
    const lvl = capitalize(level);
    const levelKey = `securitySnapshotLevels${lvl}` satisfies ContentEntryRefsKey;
    return [
      level as UserLevels,
      {
        number: index + 1,
        level: <NamedContentEntryText container="span" refKey={levelKey} subKey="levelN" />,
        name: <NamedContentEntryText container="span" refKey={levelKey} subKey="levelName" />,
        introText: <NamedContentEntryText container="span" refKey={levelKey} subKey="bodyHeading" />,
        description: <NamedContentEntryText container="span" refKey={levelKey} subKey="bodyMain" asHtml />,
        image: <RobotAvatar levelId={lvl} />,
        color: UserLevelsColorMap[level as UserLevels],
        namePillBackground: UserLevelsNamePillColorMap[level as UserLevels].background,
        namePillColor: UserLevelsNamePillColorMap[level as UserLevels].color,
      },
    ] as const;
  }),
) as unknown as UserLevelDetailsType;

export const getUserLevelDetails = (
  levelId: string | null | undefined,
): UserLevelDetailsType[UserLevels] | undefined =>
  levelId && levelId !== NO_USER_LEVEL ? UserLevelDetails[levelId as UserLevels] : undefined;

export const getDisplayedUserLevelDetails = (levelId: string | null | undefined): UserLevelDetailsType[UserLevels] =>
  getUserLevelDetails(levelId) ?? UserLevelDetails[UserLevels.vulnerable];
